/**
 * Provider contracts for market data (prices, FX) and bank feeds, plus shared helpers.
 * Providers never do their own I/O: the caller injects `fetch` (and credentials) through `ProviderContext`,
 * so the same code runs in the API server, a worker, or tests with a mocked fetch.
 */
import { z } from 'zod';
import { KeelError } from '../errors.js';
import { Decimal, decimalToCents } from '../money.js';
import { isISODate } from '../dates.js';
// ───────────────────────────── helpers ─────────────────────────────
const redact = (url) => url.replace(/(api_token|apikey|apiKey|token)=[^&]*/g, '$1=***');
/** GET JSON through the injected fetch; network, HTTP and JSON failures become KeelError('PROVIDER'). */
export const getJson = async (ctx, providerId, url) => {
    let res;
    try {
        res = await ctx.fetch(url, { headers: { accept: 'application/json' } });
    }
    catch (e) {
        throw new KeelError('PROVIDER', `${providerId}: request failed`, { url: redact(url), cause: String(e) });
    }
    if (!res.ok)
        throw new KeelError('PROVIDER', `${providerId}: HTTP ${res.status}`, { url: redact(url), status: res.status });
    try {
        return await res.json();
    }
    catch {
        throw new KeelError('PROVIDER', `${providerId}: invalid JSON`, { url: redact(url) });
    }
};
/** Validate a provider payload; shape mismatches become KeelError('PROVIDER'). */
export const parseOrThrow = (schema, body, providerId) => {
    const r = schema.safeParse(body);
    if (!r.success)
        throw new KeelError('PROVIDER', `${providerId}: unexpected response shape`, { issues: r.error.issues.slice(0, 5) });
    return r.data;
};
export const requireKey = (ctx, providerId) => {
    if (!ctx.apiKey)
        throw new KeelError('PROVIDER', `${providerId}: API key required`);
    return ctx.apiKey;
};
/** Sub-unit currencies some providers quote in (LSE pence etc.). */
const SUBUNITS = { GBp: 'GBP', GBX: 'GBP', ZAc: 'ZAR', ILA: 'ILS' };
export const normaliseCurrency = (currency) => (Object.hasOwn(SUBUNITS, currency) ? SUBUNITS[currency] : undefined) ?? currency.toUpperCase();
/** Provider decimal price → minor units, normalising sub-unit currencies (GBp 512.3 pence → 512 GBP minor units). */
/** Provider price → minor units. Non-numeric, non-finite or out-of-range values throw KeelError('PROVIDER'). */
export const priceToCents = (value, currency) => {
    const sub = Object.hasOwn(SUBUNITS, currency) ? SUBUNITS[currency] : undefined;
    const ccy = normaliseCurrency(currency);
    let cents;
    try {
        cents = decimalToCents(sub ? new Decimal(value).div(100) : value, ccy);
    }
    catch {
        cents = NaN;
    }
    if (!Number.isSafeInteger(cents))
        throw new KeelError('PROVIDER', `Invalid price value ${JSON.stringify(String(value)).slice(0, 40)}`);
    return { cents, currency: ccy };
};
// Shared response primitives: anything outside these shapes becomes KeelError('PROVIDER') via parseOrThrow.
export const NumericString = z.string().regex(/^-?\d{1,15}(\.\d{1,10})?$/, 'numeric string');
export const IsoDateString = z.string().refine(isISODate, 'YYYY-MM-DD');
/** Unix seconds between 1970 and 3000. */
export const EpochSeconds = z.number().int().min(0).max(32_503_680_000);
/** Validate a requested date range before it is interpolated into a provider URL. */
export const assertDateRange = (providerId, from, to) => {
    if (!isISODate(from) || !isISODate(to) || from > to) {
        throw new KeelError('VALIDATION', `${providerId}: invalid date range ${JSON.stringify(from)}..${JSON.stringify(to)}`);
    }
};
/**
 * Exchange → provider symbol suffix. Yahoo and EODHD suffixes are well established; Alpha Vantage's are from its docs
 * (`.LON`, `.TRT`, `.DEX`) — ponytail: ASX/NZX coverage on Alpha Vantage is patchy; prefer a `providerSymbol`
 * captured from SYMBOL_SEARCH when it differs.
 */
export const EXCHANGE_SUFFIXES = {
    ASX: { currency: 'AUD', yahoo: '.AX', eodhd: '.AU', alphavantage: '.AX' },
    NYSE: { currency: 'USD', yahoo: '', eodhd: '.US', alphavantage: '' },
    NASDAQ: { currency: 'USD', yahoo: '', eodhd: '.US', alphavantage: '' },
    LSE: { currency: 'GBP', yahoo: '.L', eodhd: '.LSE', alphavantage: '.LON' },
    NZX: { currency: 'NZD', yahoo: '.NZ', eodhd: '.NZ', alphavantage: null },
    TSX: { currency: 'CAD', yahoo: '.TO', eodhd: '.TO', alphavantage: '.TRT' },
    XETRA: { currency: 'EUR', yahoo: '.DE', eodhd: '.XETRA', alphavantage: '.DEX' },
};
const isSuffixProvider = (p) => p === 'yahoo' || p === 'eodhd' || p === 'alphavantage';
/**
 * Provider symbol for a security. An explicit `providerSymbol` wins when it belongs to this provider (or no provider
 * is recorded). Yahoo: `VAS.AX`, `BP.L`, `AAPL`, indices `^AXJO` as-is. EODHD: `VAS.AU`, `AAPL.US`, `AXJO.INDX`.
 * Alpha Vantage: `AAPL`, `TSCO.LON`. Unknown exchange/provider: `ASX:VAS`.
 */
export const symbolFor = (security, providerId) => {
    if (security.providerSymbol && (!security.priceProvider || security.priceProvider === providerId))
        return security.providerSymbol;
    const code = security.code.toUpperCase();
    const ex = security.exchange.toUpperCase();
    if (code.startsWith('^'))
        return providerId === 'eodhd' ? `${code.slice(1)}.INDX` : code;
    const suffix = isSuffixProvider(providerId) ? EXCHANGE_SUFFIXES[ex]?.[providerId] : undefined;
    return suffix === undefined || suffix === null ? `${ex}:${code}` : `${code}${suffix}`;
};
/** Reverse lookup: exchange whose suffix the provider symbol carries (bare symbols → US for Yahoo/AV). */
export const exchangeForSymbol = (providerId, symbol) => {
    for (const [ex, info] of Object.entries(EXCHANGE_SUFFIXES)) {
        const s = info[providerId];
        if (s && symbol.toUpperCase().endsWith(s.toUpperCase()))
            return ex;
    }
    return providerId !== 'eodhd' && !symbol.includes('.') && !symbol.startsWith('^') ? 'NYSE' : null;
};
/** Currency implied by a provider symbol's suffix, or null when unknown. */
export const currencyForSymbol = (providerId, symbol) => {
    const ex = exchangeForSymbol(providerId, symbol);
    return ex ? EXCHANGE_SUFFIXES[ex].currency : null;
};
/** Strip a provider suffix: `VAS.AX` → `VAS`. */
export const codeFromSymbol = (symbol) => symbol.replace(/\.[A-Z]{1,6}$/i, '');
//# sourceMappingURL=provider.js.map